import { Controller } from "@hotwired/stimulus"
export default class extends Controller {
  static targets = ["input", "dropArea", "dragOverlay", "list", "template", "error", "tagField"]
  static values = { createUrl: String }

  connect() {
    this.allowedTypes = ["image/jpeg", "image/png", "video/mp4"]
  }

  handleFileSelect(event) {
    const files = Array.from(event.target.files || [])
    this.queueFiles(files)
    // reset the input so the same file can be selected twice if needed
    event.target.value = ""
  }

  openFilePicker() {
    this.inputTarget.click()
  }

  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dragOverlayTarget.classList.remove("hidden")
    this.dragOverlayTarget.classList.add("flex")
  }

  handleDragEnter(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dragOverlayTarget.classList.remove("hidden")
    this.dragOverlayTarget.classList.add("flex")
  }

  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dragOverlayTarget.classList.add("hidden")
    this.dragOverlayTarget.classList.remove("flex")
  }

  handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dragOverlayTarget.classList.add("hidden")
    this.dragOverlayTarget.classList.remove("flex")
    const files = Array.from(event.dataTransfer.files || [])
    this.queueFiles(files)
    return false
  }

  handleWindowDragOver(event) {
    if (this.containsFiles(event)) {
      event.preventDefault()
      event.stopPropagation()
    }
  }

  handleWindowDrop(event) {
    if (this.containsFiles(event)) {
      event.preventDefault()
      event.stopPropagation()
      this.dragOverlayTarget.classList.add("hidden")
      this.dragOverlayTarget.classList.remove("flex")
    }
  }

  queueFiles(files) {
    if (!files.length) return

    // Clear placeholder text on first real file
    if (this.listTarget.dataset.empty === "true") {
      this.listTarget.innerHTML = ""
      this.listTarget.dataset.empty = "false"
    }

    files.forEach((file) => {
      if (!this.allowedTypes.includes(file.type)) {
        this.showError(`Unsupported type: ${file.type || file.name}`)
        return
      }
      this.startUpload(file)
    })
  }

  startUpload(file) {
    const card = this.buildCard(file)
    this.performUpload(file, card)
  }

  performUpload(file, card) {
    const tagNames = this.hasTagFieldTarget ? this.tagFieldTarget.value : ""
    const token = document.querySelector("meta[name='csrf-token']")?.getAttribute("content")

    const formData = new FormData()
    formData.append("media_item[file]", file)
    formData.append("media_item[name]", file.name)
    formData.append("tag_names", tagNames)

    this.updateStatus(card, "Uploading...")

    fetch(this.createUrlValue, {
      method: "POST",
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": token
      },
      body: formData
    })
      .then(async (response) => {
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}))
          throw new Error(payload.errors?.join(", ") || payload.error || "Upload failed")
        }
        return response.json()
      })
      .then((data) => {
        this.updateStatus(card, "Uploaded")
        const meta = card.querySelector("[data-meta]")
        if (meta) meta.textContent = data.media_type
        const thumb = card.querySelector("[data-thumbnail] img")
        if (thumb && data.thumbnail_url) {
          thumb.src = data.thumbnail_url
        }
      })
      .catch((error) => {
        this.updateStatus(card, "Upload failed")
        this.showError(error.message)
      })
  }

  buildCard(file) {
    const template = this.hasTemplateTarget
      ? this.templateTarget
      : this.element.querySelector("[data-admin--media-upload-target='template']")

    let card
    if (template && template.content) {
      const fragment = template.content.cloneNode(true)
      card = fragment.firstElementChild
    } else {
      card = document.createElement("div")
      card.className = "bg-muted/40 border border-border rounded-lg p-4 space-y-3"
      card.innerHTML = `
        <div class="flex items-center gap-3">
          <div class="w-16 h-16 rounded-md bg-background overflow-hidden flex items-center justify-center border border-border" data-thumbnail>
            <div class="text-muted-foreground">Upload</div>
          </div>
          <div class="flex-1">
            <div class="text-sm font-semibold text-card-foreground truncate" data-filename></div>
            <div class="text-xs text-muted-foreground" data-meta></div>
          </div>
        </div>
        <div class="w-full bg-border rounded-full h-2 overflow-hidden">
          <div class="h-full bg-primary transition-all duration-150" style="width: 0%;" data-progress></div>
        </div>
        <div class="flex items-center justify-between text-xs">
          <span class="text-muted-foreground" data-status>Queued</span>
          <button type="button" class="text-destructive hover:underline" data-remove>Remove</button>
        </div>
      `
    }

    card.querySelector("[data-filename]").textContent = file.name
    card.querySelector("[data-meta]").textContent = `${this.humanSize(file.size)} • ${file.type || "file"}`

    const removeButton = card.querySelector("[data-remove]")
    removeButton.addEventListener("click", () => card.remove())

    const thumbWrapper = card.querySelector("[data-thumbnail]")
    if (file.type.startsWith("image/")) {
      const img = document.createElement("img")
      img.className = "w-full h-full object-cover"
      img.alt = file.name
      thumbWrapper.innerHTML = ""
      thumbWrapper.appendChild(img)

      const reader = new FileReader()
      reader.onload = (event) => {
        img.src = event.target.result
      }
      reader.readAsDataURL(file)
    } else {
      thumbWrapper.innerHTML = `<div class="text-xs text-muted-foreground text-center px-2">Video</div>`
    }

    this.listTarget.appendChild(card)
    return card
  }

  updateProgress(card, percent) {
    const bar = card.querySelector("[data-progress]")
    if (bar) bar.style.width = `${percent}%`
    this.updateStatus(card, `${percent}%`)
  }

  updateStatus(card, text) {
    const status = card.querySelector("[data-status]")
    if (status) status.textContent = text
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("hidden")
  }

  humanSize(bytes) {
    if (!bytes) return "0 B"
    const thresh = 1024
    if (Math.abs(bytes) < thresh) {
      return `${bytes} B`
    }
    const units = ["KB", "MB", "GB", "TB"]
    let u = -1
    do {
      bytes /= thresh
      ++u
    } while (Math.abs(bytes) >= thresh && u < units.length - 1)
    return `${bytes.toFixed(1)} ${units[u]}`
  }

  containsFiles(event) {
    return event.dataTransfer?.types?.includes("Files")
  }

  get hasTemplateTarget() {
    return this.targets?.hasOwnProperty("template")
  }
}
